import React, { useState } from 'react';
import { FormControl, Select, Typography } from '@mui/material';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import { AppContext } from '../../../App';
import { useQuery } from '@tanstack/react-query';
import DownloadIcon from '@mui/icons-material/Download';
import { capitalizeFirstLetter } from '../../../utils/capitalizeFirstLetter';
import { IExtractionBatch } from '../../../types/my-types';
import MenuItemHiddenTextLabel from '../../common/MenuItemHiddenTextLabel';

import errorResponses from '../../../errorResponses';
import { saveAs } from 'file-saver';
import useGlynt from '../../hooks/useGlynt';
import useGetPackager from '../../hooks/packager/useGetPackager';
import { TemplateReportType } from '../../../types/packager-types';

import IconButtonWhiteBg from '../../common/IconButtonWhiteBg';

export interface IExtractionBatchDownloadModalProps {
  extractionBatch: IExtractionBatch;
  ebFinished: boolean;
  type: 'menu' | 'icon';
  handleClose?: () => void;
}

type TDownloadFormat = 'csv' | 'json';

const ExtractionBatchDownloadModal: React.FC<IExtractionBatchDownloadModalProps> = ({
  extractionBatch,
  ebFinished,
  type,
  handleClose,
}) => {
  const { setToast, isStaff, dataPoolId } = React.useContext(AppContext);
  const glynt = useGlynt();
  const [openDialog, setOpenDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState<TDownloadFormat>('csv');
  const [downloadType, setDownloadType] = useState('transformed');
  const { packager } = useGetPackager(dataPoolId);
  const templates = packager?.custom_output_config?.templates ?? {};
  const templateOptions = Object.keys(templates).filter(
    (name) =>
      (templates[name].report_type ?? TemplateReportType.ACTIVITY) === TemplateReportType.ACTIVITY
  );
  // A packager activity template is one more download type; the prefix keeps its
  // value apart from the built-in types (GP-2144).
  const TEMPLATE_PREFIX = 'template:';
  const template = downloadType.startsWith(TEMPLATE_PREFIX)
    ? downloadType.slice(TEMPLATE_PREFIX.length)
    : undefined;

  const csvOptions = ['transformed', 'wide', 'stats', isStaff ? 'long' : null];
  const jsonOptions = ['transformed', 'default'];

  const { refetch: refetchDistributionCsv } = useQuery({
    queryKey: ['distributionReport'],

    queryFn: async () => {
      return await (
        await glynt
      ).extractionBatch.distribution(extractionBatch.id, {
        advanced: true,
        format: 'csv',
        report_type: downloadType,
      });
    },

    enabled: false,
  });

  const { refetch: refetchDistributionJson } = useQuery({
    queryKey: ['distributionReport'],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.distribution(extractionBatch.id, {
        advanced: true,
      }),

    enabled: false,
  });

  const { refetch: refetchTransformCsv } = useQuery({
    queryKey: ['transformerCsv', extractionBatch.id, template],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.transformer(extractionBatch.id, { format: 'csv', template }),

    enabled: false,
  });

  const { refetch: refetchTransformJson } = useQuery({
    queryKey: ['transformerJson', extractionBatch.id, template],

    queryFn: async () =>
      await (
        await glynt
      ).extractionBatch.transformer(extractionBatch.id, { format: 'json', template }),

    enabled: false,
  });

  const downloadReport = async (format: TDownloadFormat, option: string) => {
    setIsLoading(true);

    setToast({
      variant: 'download',
      open: true,
      message: `Downloading ${format.toUpperCase()}...`,
    });

    let refetchFunc;
    // Choose the appropriate refetch function based on the format and selected option.
    if (option === 'transformed' || template) {
      refetchFunc = format === 'csv' ? () => refetchTransformCsv() : () => refetchTransformJson();
    } else {
      refetchFunc =
        format === 'csv' ? () => refetchDistributionCsv() : () => refetchDistributionJson();
    }

    try {
      let result = await refetchFunc();
      if (result.isSuccess && result.data) {
        // Construct the blob and filename for download.
        const blobType =
          format === 'csv' ? 'application/csv;charset=utf-8' : 'application/json;charset=utf-8';
        let filename;

        if (option === 'transformed' || template) {
          filename = template
            ? `${extractionBatch.label}-${template}-${extractionBatch.id}.${format}`
            : `${extractionBatch.label}-extraction-transformed-${extractionBatch.id}.${format}`;
        } else {
          filename = `${extractionBatch.label}-extraction-${option}-report-${extractionBatch.id}.${format}`;
        }

        const responseData = format === 'csv' ? result.data : JSON.stringify(result.data);
        const blob = new Blob([responseData as BlobPart], {
          type: blobType,
        });
        saveAs(blob, filename);
        setToast({ open: false });

        setToast({
          variant: 'success',
          open: true,
          message: `Download Successful`,
        });
      } else {
        handleDownloadError(result.error, format);
      }
    } catch (error) {
      handleDownloadError(error, format);
    } finally {
      setIsLoading(false);
    }
  };

  // Handles any errors that might occur during the report download.
  // Displays an error message in the application's context.
  const handleDownloadError = (error: any, format: any) => {
    let message = `An error occurred while retrieving ${format.toUpperCase()} data. Please try again or contact your Glynt representative.`;
    if (
      error instanceof Error &&
      error.message.includes('The batch contains transformations that were not found')
    ) {
      message = errorResponses.transformationNotFound;
    }
    setToast({
      variant: 'error',
      open: true,
      message,
    });
  };

  const runHandleClose = () => {
    if (handleClose) {
      handleClose();
    }
  };

  const handleClickOpen = () => {
    setOpenDialog(true);
  };

  const handleCloseConfirmation = () => {
    setOpenDialog(false);
    runHandleClose();
  };

  const handleConfirm = async () => {
    await downloadReport(downloadFormat, downloadType);
    setOpenDialog(false);
    runHandleClose();
  };

  return (
    <>
      {type === 'menu' ? (
        <MenuItem
          key={'Download'}
          onClick={handleClickOpen}
          sx={{ fontSize: '0.875rem', padding: '4px 8px' }}
          disabled={!ebFinished || extractionBatch.status === 'Failed'}
          data-cy={`btn-extraction-download-${extractionBatch.label}`}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <MenuItemHiddenTextLabel>Download</MenuItemHiddenTextLabel>
        </MenuItem>
      ) : type === 'icon' ? (
        <IconButtonWhiteBg
          disabled={!ebFinished || extractionBatch.status === 'Failed'}
          onClick={handleClickOpen}
          id="icon-extraction-download"
          cypressId="icon-extraction-download"
          tooltip="Download"
        >
          <DownloadIcon style={{ fontSize: '15px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : null}

      <DialogGlynt
        open={openDialog}
        closeModal={handleCloseConfirmation}
        cypressId="dialog-download-eb"
        loading={isLoading}
      >
        <DialogTitleGlynt
          title="Download EB Data"
          closeModal={handleCloseConfirmation}
          loading={isLoading}
        />
        <DialogContentGlynt>
          <FormControl fullWidth sx={{ mb: 1.5 }}>
            <FormLabelStyled>Download Format</FormLabelStyled>
            <Select
              labelId="download-type-label"
              id="download-type"
              variant="standard"
              value={downloadFormat}
              data-cy="download-format-select"
              onChange={(e) => {
                setDownloadFormat(e.target.value as TDownloadFormat);
                setDownloadType('transformed');
              }}
            >
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!downloadFormat}>
            <FormLabelStyled>Download Type</FormLabelStyled>
            <Select
              labelId="download-option-label"
              id="download-option"
              variant="standard"
              value={downloadType}
              data-cy="download-option-select"
              onChange={(e) => setDownloadType(e.target.value as string)}
            >
              {downloadFormat === 'csv'
                ? csvOptions.map((option) => {
                    if (!option) return null;
                    return (
                      <MenuItem key={option} value={option}>
                        {capitalizeFirstLetter(option)}
                      </MenuItem>
                    );
                  })
                : downloadFormat === 'json'
                  ? jsonOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {capitalizeFirstLetter(option)}
                      </MenuItem>
                    ))
                  : null}
              {templateOptions.map((name) => (
                <MenuItem key={name} value={`${TEMPLATE_PREFIX}${name}`}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleConfirm}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-extraction-download-cancel"
          submitCypressId="btn-extraction-download-ok"
          clearCypressId=""
          submitButtonText="Download"
          submitLoading={isLoading}
        />
      </DialogGlynt>
    </>
  );
};

export default ExtractionBatchDownloadModal;
